import React, { useMemo, useRef, useState } from "react";
import { useAdminEvent } from "@/context/AppContext";
import { api } from "@/lib/api";
import { PageIntro } from "@/admin/ui";
import {
  UploadCloud, FileSpreadsheet, CheckCircle2, AlertTriangle, RefreshCw,
  ShieldCheck, ArrowRight, Users, KeyRound, UserPlus, EyeOff, ListChecks,
} from "lucide-react";
import { toast } from "sonner";

const APPLY_TOKEN = "APPLY_TM_APP_UPLOAD";

const STEPS = ["Upload", "Analyze", "Review", "Apply"];

export default function TravelMasterUpload() {
  const { eventId } = useAdminEvent();
  const [step, setStep] = useState(0); // 0 upload, 1 analyzing, 2 review, 3 applied
  const [job, setJob] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [applied, setApplied] = useState(null);
  const fileRef = useRef(null);

  const reset = () => {
    setStep(0); setJob(null); setAnalysis(null); setConfirm(""); setApplied(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const onUpload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".xlsx")) { toast.error("Please upload the .xlsx workbook"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const j = await api.tmAppUpload(eventId, fd);
      setJob(j);
      toast.success(`Loaded ${j.row_count} rows from ${j.sheet}`);
      // auto-run analyze
      setStep(1);
      const a = await api.tmAppAnalyze(eventId, j.job_id);
      setAnalysis(a);
      setStep(2);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload/analyze failed");
      setStep(0);
    } finally { setBusy(false); }
  };

  const reAnalyze = async () => {
    if (!job) return;
    setBusy(true);
    try {
      const a = await api.tmAppAnalyze(eventId, job.job_id);
      setAnalysis(a);
      toast.success("Re-analyzed (read-only)");
    } catch (e) { toast.error(e?.response?.data?.detail || "Analyze failed"); }
    finally { setBusy(false); }
  };

  const onApply = async () => {
    if (confirm !== APPLY_TOKEN) { toast.error("Type the confirmation token exactly to apply"); return; }
    setBusy(true);
    try {
      const res = await api.tmAppApply(eventId, {
        job_id: job.job_id, plan_signature: analysis.plan_signature, confirm,
      });
      setApplied(res);
      setStep(3);
      toast.success("Applied to the roster");
    } catch (e) { toast.error(e?.response?.data?.detail || "Apply failed"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="tm-app-upload-page">
      <PageIntro>
        <b>PC Master Data</b> is the canonical attendee roster importer. Upload the
        <b> APP_UPLOAD</b> sheet from the PC2026 workbook. The safe workflow is
        <b> Upload → Analyze → Review → Apply</b>. Analyze never writes to the roster;
        Apply is idempotent and requires an explicit confirmation.
      </PageIntro>

      <Stepper step={step} />

      {step === 0 && (
        <Dropzone busy={busy} fileRef={fileRef} onUpload={onUpload} />
      )}

      {step === 1 && (
        <div className="premium-card p-8 text-center" data-testid="tm-analyzing">
          <RefreshCw className="h-6 w-6 mx-auto animate-spin text-[hsl(var(--secondary))]" />
          <p className="mt-3 text-sm text-[hsl(var(--muted-foreground))]">Analyzing {job?.row_count} rows against the current roster…</p>
        </div>
      )}

      {step === 2 && analysis && (
        <Review
          job={job} analysis={analysis} busy={busy}
          confirm={confirm} setConfirm={setConfirm}
          onApply={onApply} onReAnalyze={reAnalyze} onReset={reset}
        />
      )}

      {step === 3 && applied && (
        <Applied applied={applied} onReset={reset} onReAnalyze={reAnalyze} />
      )}
    </div>
  );
}

function Stepper({ step }) {
  return (
    <div className="flex items-center gap-2 mb-4" data-testid="tm-stepper">
      {STEPS.map((s, i) => (
        <React.Fragment key={s}>
          <div className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold ${i <= step ? "bg-[hsl(var(--secondary)/0.14)] text-[hsl(var(--secondary))]" : "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]"}`}>
            <span className={`grid place-items-center h-5 w-5 rounded-full text-[10px] ${i <= step ? "bg-[hsl(var(--secondary))] text-white" : "bg-[hsl(var(--border))] text-[hsl(var(--muted-foreground))]"}`}>{i + 1}</span>
            {s}
          </div>
          {i < STEPS.length - 1 && <ArrowRight className="h-3.5 w-3.5 text-[hsl(var(--muted-foreground))]" />}
        </React.Fragment>
      ))}
    </div>
  );
}

function Dropzone({ busy, fileRef, onUpload }) {
  return (
    <div className="premium-card p-8" data-testid="tm-dropzone">
      <div className="flex flex-col items-center text-center">
        <div className="h-14 w-14 rounded-2xl bg-[hsl(var(--secondary)/0.12)] grid place-items-center">
          <UploadCloud className="h-7 w-7 text-[hsl(var(--secondary))]" />
        </div>
        <h3 className="mt-3 font-serif text-xl text-[hsl(var(--primary))]">Upload PC Master Data workbook</h3>
        <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))] max-w-md">
          The importer reads the <b>APP_UPLOAD</b> sheet only. The MASTER sheet is ignored.
          Columns are mapped by header name — unknown columns won't break the import.
        </p>
        <label className="mt-4 inline-flex items-center gap-2 rounded-xl bg-[hsl(var(--primary))] text-white px-5 py-2.5 text-sm font-semibold hover:opacity-90 cursor-pointer">
          <FileSpreadsheet className="h-4 w-4" /> {busy ? "Uploading…" : "Choose .xlsx file"}
          <input ref={fileRef} data-testid="tm-file-input" type="file" accept=".xlsx" disabled={busy}
            className="hidden" onChange={(e) => onUpload(e.target.files?.[0])} />
        </label>
      </div>
    </div>
  );
}

function Stat({ label, value, testid, tone = "default", icon: Icon }) {
  const tones = {
    default: "text-[hsl(var(--primary))]",
    good: "text-[hsl(var(--secondary))]",
    warn: "text-[hsl(38_50%_34%)]",
    bad: "text-[hsl(0_60%_45%)]",
  };
  return (
    <div className="rounded-xl border border-[hsl(var(--border))] bg-white p-3">
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-[hsl(var(--muted-foreground))] font-semibold">
        {Icon && <Icon className="h-3.5 w-3.5" />}{label}
      </div>
      <div data-testid={testid} className={`mt-1 font-serif text-2xl ${tones[tone]}`}>{value}</div>
    </div>
  );
}

function CountRow({ title, map, order }) {
  const keys = order || Object.keys(map);
  return (
    <div>
      <div className="text-xs font-semibold text-[hsl(var(--primary))] mb-1.5">{title}</div>
      <div className="flex flex-wrap gap-1.5">
        {keys.map((k) => (
          <span key={k} className="inline-flex items-center gap-1 rounded-full border border-[hsl(var(--border))] px-2.5 py-1 text-xs">
            <span className="text-[hsl(var(--muted-foreground))]">{k}</span>
            <b className="text-[hsl(var(--primary))]">{map[k] ?? 0}</b>
          </span>
        ))}
      </div>
    </div>
  );
}

function Review({ job, analysis, busy, confirm, setConfirm, onApply, onReAnalyze, onReset }) {
  const ss = analysis.source_summary;
  const rec = analysis.reconciliation;
  const cc = analysis.classification_counts;
  const status = analysis.status_counts || {};
  const [filter, setFilter] = useState("changes"); // changes | all | new | review | error | missing
  const hasBlockers = ss.invalid_rows > 0 || (ss.duplicate_emails?.length > 0) || (ss.duplicate_attendee_keys?.length > 0);

  const rows = useMemo(() => {
    let r = analysis.results;
    if (filter === "changes") r = r.filter((x) => x.changes?.length > 0 || x.status === "new");
    else if (filter === "new") r = r.filter((x) => x.status === "new");
    else if (filter === "review") r = r.filter((x) => x.status === "review");
    else if (filter === "error") r = r.filter((x) => x.status === "error");
    return r;
  }, [analysis.results, filter]);

  return (
    <div className="space-y-4" data-testid="tm-review">
      {/* Source summary */}
      <div className="premium-card p-4">
        <div className="flex items-center gap-2 mb-3">
          <ListChecks className="h-4 w-4 text-[hsl(var(--secondary))]" />
          <h3 className="font-semibold text-[hsl(var(--primary))]">Source — {job?.file_name} · {job?.sheet}</h3>
          <button onClick={onReAnalyze} disabled={busy} data-testid="tm-reanalyze" className="ml-auto inline-flex items-center gap-1.5 text-xs font-semibold text-[hsl(var(--secondary))] disabled:opacity-50"><RefreshCw className="h-3.5 w-3.5" /> Re-analyze</button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Stat label="Total rows" value={ss.total_rows} testid="tm-total-rows" />
          <Stat label="Valid rows" value={ss.valid_rows} testid="tm-valid-rows" tone="good" />
          <Stat label="Invalid rows" value={ss.invalid_rows} testid="tm-invalid-rows" tone={ss.invalid_rows ? "bad" : "default"} />
          <Stat label="Dup emails / keys" value={`${ss.duplicate_emails.length} / ${ss.duplicate_attendee_keys.length}`} testid="tm-dups" tone={hasBlockers ? "bad" : "default"} />
        </div>
      </div>

      {/* Reconciliation */}
      <div className="premium-card p-4">
        <div className="flex items-center gap-2 mb-3">
          <Users className="h-4 w-4 text-[hsl(var(--secondary))]" />
          <h3 className="font-semibold text-[hsl(var(--primary))]">Reconciliation</h3>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          <Stat label="Matched (key)" value={rec.matched_by_attendee_key} testid="tm-matched-key" icon={KeyRound} />
          <Stat label="Matched (email)" value={rec.matched_by_email} testid="tm-matched-email" />
          <Stat label="Adopted manual" value={rec.adopted_manual_records} testid="tm-adopted" tone="good" />
          <Stat label="New attendees" value={rec.proposed_new_attendees} testid="tm-new" icon={UserPlus} tone="warn" />
          <Stat label="Needs review" value={rec.records_requiring_manual_review} testid="tm-review-count" tone={rec.records_requiring_manual_review ? "warn" : "default"} />
          <Stat label="Missing from source" value={rec.missing_from_latest_source_count} testid="tm-missing" tone={rec.missing_from_latest_source_count ? "warn" : "default"} />
        </div>
        {rec.missing_from_latest_source_count > 0 && (
          <div className="mt-3 rounded-xl border border-[hsl(var(--pc-gold)/0.5)] bg-[hsl(var(--pc-gold)/0.08)] p-3" data-testid="tm-missing-list">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-[hsl(38_50%_34%)]"><AlertTriangle className="h-3.5 w-3.5" /> On roster but missing from latest source — review required (never auto-deleted)</div>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {rec.missing_from_latest_source.map((m) => (
                <span key={m.id} className="text-xs rounded-full bg-white border border-[hsl(var(--border))] px-2.5 py-1">{m.name} · {m.email}</span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Classification + T-shirt */}
      <div className="premium-card p-4 space-y-3">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-[hsl(var(--secondary))]" />
          <h3 className="font-semibold text-[hsl(var(--primary))]">Classification & Operational counts</h3>
          <span className="ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-wide rounded-full bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] px-2 py-0.5 font-semibold"><EyeOff className="h-3 w-3" /> T-Shirt is manager-only</span>
        </div>
        <div className="grid gap-3">
          <CountRow title="RSVP" map={cc.rsvp} order={["attending", "pending", "declined"]} />
          <CountRow title="Attendee Type" map={cc.attendee_type} order={["PC Attendee", "Non-PC Attendee"]} />
          <CountRow title="Recognition" map={cc.recognition} order={["PC Winner", "MDC Winner", "Sprint to PC Winner", "Upcoming Talent Winner", "Milestone Achiever", "None"]} />
          <CountRow title="Leadership" map={cc.leadership} order={["Executive Leadership", "Leadership", "None"]} />
          <CountRow title="T-Shirt" map={cc.t_shirt} order={["XS", "S", "M", "L", "XL", "XXL", "XXXL", "NA", "MISSING"]} />
        </div>
      </div>

      {/* Per-attendee diffs */}
      <div className="premium-card p-4">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <h3 className="font-semibold text-[hsl(var(--primary))]">Proposed changes</h3>
          <div className="ml-auto flex flex-wrap gap-1.5 text-xs">
            {[["changes", `Changes (${analysis.results.filter((x) => x.changes?.length || x.status === "new").length})`],
              ["new", `New (${status.new || 0})`],
              ["review", `Review (${status.review || 0})`],
              ["error", `Errors (${status.error || 0})`],
              ["all", `All (${analysis.results.length})`]].map(([k, l]) => (
              <button key={k} data-testid={`tm-filter-${k}`} onClick={() => setFilter(k)} className={`rounded-full border px-3 py-1 font-semibold ${filter === k ? "border-[hsl(var(--secondary))] text-[hsl(var(--secondary))] bg-[hsl(var(--secondary)/0.1)]" : "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))]"}`}>{l}</button>
            ))}
          </div>
        </div>
        <div className="divide-y divide-[hsl(var(--border))] max-h-[420px] overflow-y-auto no-scrollbar" data-testid="tm-diff-list">
          {rows.map((r) => <DiffRow key={r.row} r={r} />)}
          {rows.length === 0 && <div className="py-8 text-center text-sm text-[hsl(var(--muted-foreground))]">No rows match this filter.</div>}
        </div>
      </div>

      {/* Apply */}
      <div className="premium-card p-4" data-testid="tm-apply-card">
        <div className="flex items-center gap-2 mb-2">
          <CheckCircle2 className="h-4 w-4 text-[hsl(var(--secondary))]" />
          <h3 className="font-semibold text-[hsl(var(--primary))]">Apply to roster</h3>
        </div>
        {hasBlockers ? (
          <div className="rounded-xl border border-[hsl(0_60%_80%)] bg-[hsl(0_60%_97%)] p-3 text-sm text-[hsl(0_60%_40%)]" data-testid="tm-apply-blocked">
            <AlertTriangle className="h-4 w-4 inline mr-1.5" /> Resolve invalid rows / duplicates in the source file before applying.
          </div>
        ) : (
          <>
            <p className="text-xs text-[hsl(var(--muted-foreground))] mb-2">
              Apply is idempotent and only writes changed fields. Attendees missing from the source are never deleted.
              Type <code className="px-1 rounded bg-[hsl(var(--muted))] font-mono">{APPLY_TOKEN}</code> to confirm.
            </p>
            <div className="flex flex-wrap gap-2">
              <input data-testid="tm-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                placeholder={APPLY_TOKEN}
                className="flex-1 min-w-[220px] px-3 py-2 rounded-lg border border-[hsl(var(--border))] text-sm font-mono" />
              <button data-testid="tm-apply-btn" disabled={busy || confirm !== APPLY_TOKEN} onClick={onApply}
                className="inline-flex items-center gap-2 rounded-xl bg-[hsl(var(--primary))] text-white px-5 py-2 text-sm font-semibold hover:opacity-90 disabled:opacity-50">
                {busy ? "Applying…" : "Apply changes"}
              </button>
              <button data-testid="tm-cancel-btn" onClick={onReset} className="rounded-xl px-4 py-2 text-sm font-semibold text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]">Start over</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const FIELD_LABEL = {
  attendee_key: "Attendee Key", email: "Email", first_name: "First Name", last_name: "Last Name",
  preferred_name: "Preferred Name", role_title: "Role / Title", rsvp_status: "RSVP",
  source_classification: "Classification", attendee_group: "Accom. category",
  planned_hotel_arrival: "Check-in", planned_hotel_departure: "Check-out",
  designated_arrival_date: "Arrival", designated_departure_date: "Departure",
  guest_exists: "Plus One", guest_first_name: "Guest First", guest_last_name: "Guest Last",
  guest_email: "Guest Email", employee_gala_meal: "Attendee Meal", guest_gala_meal: "Plus One Meal",
  room_sharer: "Room Sharer", room_category: "Room Category", company_pay: "Company Pay",
  employee_pay: "Employee Pay", comments: "Comments", t_shirt_size: "T-Shirt Size",
  source_group: "Source Group", source_pc_group: "Source PC Group",
};

function fmt(v) {
  if (v == null || v === "") return "—";
  if (typeof v === "object") {
    if (v.attendee_type || v.recognition || v.leadership) return `${v.attendee_type} · ${v.recognition} · ${v.leadership}`;
    return JSON.stringify(v);
  }
  if (v === true) return "Yes";
  if (v === false) return "No";
  return String(v);
}

function DiffRow({ r }) {
  const badge = {
    new: ["New", "bg-[hsl(var(--pc-gold)/0.15)] text-[hsl(38_50%_34%)]"],
    updated: ["Updated", "bg-[hsl(var(--secondary)/0.12)] text-[hsl(var(--secondary))]"],
    unchanged: ["Unchanged", "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]"],
    review: ["Review", "bg-[hsl(var(--pc-gold)/0.15)] text-[hsl(38_50%_34%)]"],
    error: ["Error", "bg-[hsl(0_60%_92%)] text-[hsl(0_60%_40%)]"],
  }[r.status] || ["", ""];
  return (
    <div className="py-2.5" data-testid="tm-diff-row">
      <div className="flex items-center gap-2">
        <span className={`text-[10px] uppercase tracking-wide rounded-full px-2 py-0.5 font-semibold ${badge[1]}`}>{badge[0]}</span>
        <span className="text-sm font-semibold text-[hsl(var(--primary))]">{r.name || "(no name)"}</span>
        <span className="text-xs text-[hsl(var(--muted-foreground))]">{r.email}</span>
        {r.match_method && <span className="text-[10px] text-[hsl(var(--muted-foreground))]">· matched by {r.match_method}</span>}
        {r.adopted_manual && <span className="text-[10px] text-[hsl(var(--secondary))] font-semibold">· adopted manual</span>}
      </div>
      {r.errors?.length > 0 && (
        <div className="mt-1 text-xs text-[hsl(0_60%_45%)]">{r.errors.join("; ")}</div>
      )}
      {r.warnings?.length > 0 && (
        <div className="mt-1 text-xs text-[hsl(38_50%_34%)]">{r.warnings.join("; ")}</div>
      )}
      {r.changes?.length > 0 && (
        <div className="mt-1.5 space-y-0.5">
          {r.changes.map((c) => (
            <div key={c.field} className="text-xs flex flex-wrap items-center gap-1.5">
              <span className="text-[hsl(var(--muted-foreground))] w-28 shrink-0">{FIELD_LABEL[c.field] || c.field}</span>
              <span className="text-[hsl(var(--muted-foreground))] line-through">{fmt(c.previous)}</span>
              <ArrowRight className="h-3 w-3 text-[hsl(var(--secondary))]" />
              <span className="font-medium text-[hsl(var(--primary))]">{fmt(c.new)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Applied({ applied, onReset, onReAnalyze }) {
  const c = applied.counts;
  return (
    <div className="premium-card p-6" data-testid="tm-applied">
      <div className="flex items-center gap-2">
        <CheckCircle2 className="h-6 w-6 text-[hsl(var(--secondary))]" />
        <h3 className="font-serif text-xl text-[hsl(var(--primary))]">Applied to the roster</h3>
      </div>
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-5 gap-2">
        <Stat label="Created" value={c.created} testid="tm-applied-created" tone="good" />
        <Stat label="Updated" value={c.updated} testid="tm-applied-updated" tone="good" />
        <Stat label="Unchanged" value={c.unchanged} testid="tm-applied-unchanged" />
        <Stat label="Keys attached" value={c.attendee_keys_attached} testid="tm-applied-keys" />
        <Stat label="Skipped (errors)" value={c.skipped_errors} testid="tm-applied-skipped" tone={c.skipped_errors ? "bad" : "default"} />
      </div>
      <p className="mt-3 text-xs text-[hsl(var(--muted-foreground))]">
        Re-run Analyze on the same file to confirm it now proposes zero changes (idempotent).
      </p>
      <div className="mt-4 flex gap-2">
        <button data-testid="tm-applied-reanalyze" onClick={onReAnalyze} className="inline-flex items-center gap-2 rounded-xl bg-[hsl(var(--secondary))] text-white px-4 py-2 text-sm font-semibold hover:brightness-110"><RefreshCw className="h-4 w-4" /> Re-analyze</button>
        <button data-testid="tm-applied-reset" onClick={onReset} className="rounded-xl px-4 py-2 text-sm font-semibold text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]">Import another file</button>
      </div>
    </div>
  );
}
